import { ArrowLeft, Image as ImageIcon, Pencil, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { AddToCartButton } from '@/components/orders/AddToCartButton';

import type { PropertyDetailMode } from '../types/property-detail.types';

interface Props {
  mode: PropertyDetailMode;
  canEdit: boolean;
  isAddingToCart: boolean;
  isInCart: boolean;
  hasPhotoset: boolean;
  onBack: () => void;
  onManageAssets: () => void;
  onAddToCart: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export function PropertyDetailHeader({
  mode, canEdit, isAddingToCart, isInCart, hasPhotoset, onBack, onManageAssets, onAddToCart, onEdit, onSave, onCancel,
}: Props) {
  return (
    <header className="flex flex-wrap items-center gap-3 bg-card border-b border-border px-6 py-4">
      <Button variant="ghost" size="icon" type="button" onClick={onBack} tooltip="Back to Properties">
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="flex-1 min-w-0">
        <Typography as="h1" variant="heading-lg" className="font-medium truncate">
          Property Details
        </Typography>
      </div>

      {mode === 'view' && (
        <div className="flex flex-wrap items-center justify-end gap-2 max-lg:basis-full max-lg:justify-start">
          {hasPhotoset && (
            <Button variant="default" onClick={onManageAssets}>
              <ImageIcon className="h-4 w-4" />
              Manage Assets
            </Button>
          )}
          <AddToCartButton
            idleVariant="outline"
            isAdding={isAddingToCart}
            isAdded={isInCart}
            onAddToCart={onAddToCart}
            // Lock width to fit the wider "Added to cart" label so the
            // button doesn't visibly jump after a successful add.
            className="min-w-[10rem]"
          />
          {canEdit && (
            <Button variant="ghost" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      )}

      {(mode === 'edit' || mode === 'saving') && (
        <div className="flex flex-wrap items-center justify-end gap-2 max-lg:basis-full max-lg:justify-start">
          <Button variant="outline" onClick={onCancel} disabled={mode === 'saving'}>Cancel</Button>
          <Button variant="default" onClick={onSave} disabled={mode === 'saving'}>
            {mode === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'saving' ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      )}
    </header>
  );
}
